import { FC, useEffect } from "react";
import { useAuthUserOrNull, useIsAuthenticated } from "@frontegg/react";
import { useNavigate } from "react-router-dom";
import { GET_USER } from "../../graphql/user";
import { useQuery } from "@apollo/client";
import { CircularProgress } from "@mui/material";
import { getImpersonationFromStorage } from "../../contexts/ImpersonationContext";
import { useAppEntitlements } from "../../hooks/useAppEntitlements";
import { lacksGateApp, meetsGate, RouteGate } from "../../utils/apps";

interface IProtectRouteProps {
  children: React.ReactNode;
  /** One of ROUTE_GATES, which the landing resolver also checks. */
  gate: RouteGate;
}

const ProtectRoute: FC<IProtectRouteProps> = ({ children, gate }) => {
  const user = useAuthUserOrNull();
  const navigate = useNavigate();
  const isAuthenticated = useIsAuthenticated();

  const { features } = useAppEntitlements();

  const impersonation = getImpersonationFromStorage();
  const isImpersonating = Boolean(impersonation);
  const impersonatedFeatures = impersonation?.target?.features ?? null;

  // Normal sessions gate on the Frontegg token; while impersonating, gate on the
  // impersonated user's resolved features. Unknown features (null) never block.
  const featureMissingForUser =
    !isImpersonating && lacksGateApp(gate, (key) => Boolean(features[key]));
  const featureMissingForImpersonated =
    isImpersonating &&
    Array.isArray(impersonatedFeatures) &&
    lacksGateApp(gate, (key) => impersonatedFeatures.includes(key));

  const { data, loading } = useQuery(GET_USER, {
    variables: { userId: user?.sub },
  });

  const passes =
    meetsGate(gate, {
      status: data?.user?.company?.status,
      role: data?.user?.role,
    }) && !featureMissingForUser;

  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated) {
      navigate("/login");
    } else if (!passes) {
      navigate("/redirect");
    }
  }, [navigate, loading, isAuthenticated, passes]);

  if (featureMissingForImpersonated)
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          padding: 24,
          textAlign: "center",
        }}
      >
        <span>Unauthorized Access.</span>
      </div>
    );

  if (loading)
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress color="primary" />
      </div>
    );

  // Mounting the children before the redirect lands would fire every query in the shell.
  if (isAuthenticated && user && passes) {
    return <>{children}</>;
  }
  return null;
};

export default ProtectRoute;
